import type { ConsolaReporter, LogOptions, ReporterOptions } from '../../types';
import { LOG_LEVEL } from '../../types/enums';
import { isPlainObject } from '../../utilities/common';

export class SimpleReporter implements ConsolaReporter {
  private config: ReporterOptions;

  private color: string;

  private defaultStyle = [
    'border-radius: 3px',
    'font-size: 14px',
    'font-weight: bold',
    'color: white',
    '-webkit-text-stroke: .6px black',
    'padding: 2px 5px',
    'margin-right: 2px',
  ].join(';');

  protected levelColorMap: Record<number, string> = {
    1: '#00BCD4', //cyan
    2: '#f39c12', //yellow
    3: '#c0392b', //red
    4: '#2ecc71', //green
    5: '#7f8c8d', //gray
  };

  constructor(config: ReporterOptions) {
    this.config = config;
    this.color = this.colorize(config.namespace);
  }

  report(message: string, options: LogOptions): void {
    const { enabled, namespace } = this.config;
    const { args = [], group, level } = options;

    if (enabled) {
      const log = this.getConsole(level);
      const color = 'color: white; padding-left: 10px;';

      const params = [];

      for (const arg of args) {
        isPlainObject(arg) ? params.push(JSON.parse(JSON.stringify(arg))) : params.push(arg);
      }

      log(
        `%c${namespace}%c${group}%c${message}`,
        `background: ${this.color}; ${this.defaultStyle}`,
        this.getStyle(level),
        color,
        ...params,
      );
    }
  }

  reportInfo(message: string, group: string, ...args: any[]) {
    this.report(message, { level: LOG_LEVEL.LOG, group, args });
  }

  reportWarning(message: string, group: string, ...args: any[]) {
    this.report(message, { level: LOG_LEVEL.WARN, group, args });
  }

  reportError(message: string, group: string, ...args: any[]) {
    this.report(message, { level: LOG_LEVEL.ERROR, group, args });
  }

  getOptions(): ReporterOptions {
    return this.config;
  }

  protected getStyle(level: LOG_LEVEL = LOG_LEVEL.DEFAULT): string {
    const bg: string = this.levelColorMap[level] ?? (this.levelColorMap[5] as string);
    return `background: ${bg}; ${this.defaultStyle}`;
  }

  private getConsole(level: number) {
    let consoleFn = console.log.bind(console);

    switch (level) {
      case LOG_LEVEL.ERROR:
        consoleFn = console.error.bind(console);
        break;
      case LOG_LEVEL.INFO:
        consoleFn = console.info.bind(console);
        break;
      case LOG_LEVEL.WARN:
        consoleFn = console.warn.bind(console);
        break;
    }

    return consoleFn;
  }

  private colorize(str: string) {
    let hash = 0;

    // biome-ignore lint/style/useBlockStatements: <explanation>
    for (let i = 0; i < str.length; hash = str.charCodeAt(i++) + ((hash << 5) - hash));
    const color = Math.floor(Math.abs(((Math.sin(hash) * 10000) % 1) * 16777216)).toString(16);
    // biome-ignore lint/style/useTemplate: <explanation>
    return '#' + Array(6 - color.length + 1).join('0') + color;
  }
}
